import { DateTime } from "luxon";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams, useLocation } from "react-router-dom";

import { Button, Divider, Grid, IconButton, Link, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RotateRightIcon from "@material-ui/icons/RotateRight";

import { PermissionedDisplay, SectionGridContainer } from "../../components/Layout";
import { LoadingOverlay } from "../../components/Navigation/LoadingOverlay";
import { KeyValueDisplay } from "../../components/Typography";
import { FleetSettingsContext, RiderContext, FleetContext } from "../../context";
import { startStopTimeString, responsiveSize } from "../../services/misc.service";
import Receipt from "./Receipt";
import { useTestIds } from "../../hooks/useTestIds";
import { ClusterNames } from "../../constants";
import { getKibanaURL } from "../../util/helpers";
import Permissions from "../../permissions";
import uuidFromUrl from "../../util/strings/uuidFromUrl";

const ReservationDetail = () => {
  const theme = useTheme();
  const {
    reservations,
    activeReservation,
    setActiveReservation,
    fetchFleet,
    reservationFleet,
    fetchReceipt,
    fetchActiveTrip,
  } = useContext(RiderContext);
  const { fetchRatePlan, activeRatePlan } = useContext(FleetSettingsContext);
  const { fleets } = useContext(FleetContext);
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [kibanaUrl, setKibanaUrl] = useState(null);
  const { riderId, reservationId } = useParams();
  const { t } = useTranslation();
  const [photoRotation, setPhotoRotation] = useState(90);
  const [photoSize, setPhotoSize] = useState(responsiveSize(1.5));
  const [photoContainerSize, setPhotoContainerSize] = useState(responsiveSize(2.5));
  const clusterName = window.REACT_APP_CLUSTER_NAME;
  const testIds = useTestIds("rider-reservation-detail");

  useEffect(() => {
    const waitForRes = async () => {
      // For detail page we always want to hit db for fresh data
      try {
        setLoading(true);
        await Promise.all([
          setActiveReservation(reservationId),
          fetchReceipt(reservationId),
          fetchActiveTrip(reservationId),
        ]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (reservationId) waitForRes();
    return async () => Promise.all([setActiveReservation(null), fetchReceipt(null)]);
  }, [reservationId]);

  useEffect(() => {
    // we need to fetch the rate plan of the fleet where the ride ends in order to serve the correct fines
    const waitForFleet = async () => {
      try {
        setLoading(true);
        let endFleet;
        if (fleets.length && activeReservation.end_fleet) {
          endFleet = fleets.find((fleet) => fleet.url === activeReservation.end_fleet);
        }
        await Promise.all([
          fetchFleet(activeReservation?.fleets[0]),
          fetchRatePlan(endFleet ? endFleet?.rate_plan_id : activeReservation?.rate_plan?.id),
        ]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (activeReservation) waitForFleet();
    return () => {
      fetchFleet(null);
      fetchRatePlan(null);
    };
  }, [activeReservation]);

  useEffect(() => {
    const waitForRatePlan = async (endFleet) => {
      try {
        setLoading(true);
        await fetchRatePlan(endFleet.rate_plan_id);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (fleets.length && activeReservation) {
      const endFleet = fleets.find((fleet) => fleet.url === activeReservation.end_fleet);
      // if the fleet where the ride ends is from where the ride started, fetch the rate plan from the end fleet
      if (endFleet && !activeReservation.fleets.includes(uuidFromUrl(endFleet.url))) {
        waitForRatePlan(endFleet);
      }
    }
  }, [fleets]);

  useEffect(() => {
    const BASE_URL = "https://reporting.linkyour.city";
    const EU_BASE_URL = "https://reporting.linkfleet.de";
    const url =
      clusterName === ClusterNames.EU
        ? getKibanaURL(EU_BASE_URL, activeReservation)
        : getKibanaURL(BASE_URL, activeReservation);

    if (activeReservation?.trip) setKibanaUrl(url);
  }, [activeReservation?.trip]);

  useEffect(() => {
    const handleResize = () => {
      setPhotoSize(responsiveSize(1.5));
      setPhotoContainerSize(responsiveSize(2.5));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleRotate = () => {
    setPhotoRotation(photoRotation < 270 ? photoRotation + 90 : 0);
  };

  const styles = {
    imageContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#1d212d",
      border: "1px solid #1d212d",
      position: "relative",
      height: photoContainerSize,
    },
    image: {
      height: photoSize,
      transform: `rotate(${photoRotation}deg)`,
      objectFit: "contain",
    },
    rotateIcon: {
      position: "absolute",
      top: "5px",
      left: "5px",
    },
    kibanaButton: {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      borderRadius: 0,
      textDecoration: "none",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
  };
  if (loading) {
    return <LoadingOverlay />;
  }
  if (!activeReservation) {
    return <Typography variant="body1">{t("Riders.ReservationDetail.RequestError")}</Typography>;
  }
  return (
    <SectionGridContainer
      spaceContents
      style={{ paddingLeft: 0, paddingRight: 0 }}
      gridProps={{ alignItems: "flex-start", direction: "row-reverse" }}
    >
      <Grid item container xs={12} style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Grid item style={{ paddingLeft: 10 }}>
          <IconButton
            edge="start"
            component={RouterLink}
            to={{
              pathname: `/riders/${riderId}/reservations`,
              state: { page: reservations.currentPage },
            }}
            style={{ marginTop: theme.spacing(-1) }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item xs={10}>
          <KeyValueDisplay
            label={t("Riders.ReservationDetail.Reservation")}
            text={`${DateTime.fromJSDate(activeReservation.start).toLocaleString(
              DateTime.DATETIME_MED_WITH_WEEKDAY,
            )}`}
            keyTypographyProps={{ variant: "h5" }}
            valueTypographyProps={{ variant: "h5" }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 10, marginTop: theme.spacing(-1) }}
        >
          <Divider />
        </Grid>
      </Grid>
      <Grid item container xs={12} md={8} spacing={1}>
        <Grid item xs={12} md={8} style={{ paddingBottom: theme.spacing(2) }}>
          <Typography variant="h5">{t("Riders.ReservationDetail.ReservationDetails")}</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <KeyValueDisplay
            label={t("Riders.ReservationDetail.Fleet")}
            text={reservationFleet?.name}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <KeyValueDisplay
            label={t("Riders.ReservationDetail.ReservationId")}
            text={activeReservation.id}
            copyToClipboard
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <KeyValueDisplay
            label={t("Riders.ReservationDetail.Distance")}
            text={`${activeReservation?.trip?.distance} kilometers`}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <KeyValueDisplay
            label={t("Riders.ReservationDetail.ReservationTime")}
            text={startStopTimeString(activeReservation.start, activeReservation.stop)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <KeyValueDisplay
            label={t("Riders.ReservationDetail.VehicleStart")}
            text={`(${activeReservation?.trip?.start_location?.coordinates[1]}, ${activeReservation?.trip?.start_location?.coordinates[0]})`}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <KeyValueDisplay
            label={t("Riders.ReservationDetail.TripId")}
            text={activeReservation?.trip?.id}
            copyToClipboard
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <KeyValueDisplay
            label={t("Riders.ReservationDetail.VehicleStop")}
            text={`(${activeReservation?.trip?.stop_location?.coordinates[1]}, ${activeReservation?.trip?.stop_location?.coordinates[0]})`}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <KeyValueDisplay
            label={t("Riders.ReservationDetail.TripTime")}
            text={startStopTimeString(
              activeReservation?.trip?.start,
              activeReservation?.trip?.stop,
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <KeyValueDisplay
            label={t("Riders.ReservationDetail.PhoneStop")}
            text={`(${activeReservation?.stop_location?.coordinates[1]}, ${activeReservation?.stop_location?.coordinates[0]})`}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <KeyValueDisplay
            label={t("Riders.ReservationDetail.TripEndMethod")}
            text={`${activeReservation?.trip?.end_method?.replaceAll("_", " ")}`}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            component={Link}
            style={styles.kibanaButton}
            href={kibanaUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("Riders.ReservationDetail.ViewKibana")}
          </Button>
        </Grid>
        {activeReservation.parking && (
          <Grid item container xs={12} md={12} spacing={2}>
            <Grid item xs={12} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}>
              <Divider />
            </Grid>
            <Grid item container xs={12} md={6}>
              <Grid item xs={12} style={{ paddingBottom: 10 }}>
                <Typography variant="h5" gutterBottom>
                  {t("Riders.ReservationDetail.ParkingPhoto")}
                </Typography>
              </Grid>
              <Grid item container xs={12} md={12} style={styles.imageContainer}>
                <img
                  src={activeReservation.parking.photo}
                  alt="Parked Scooter"
                  style={styles.image}
                />
                <IconButton onClick={() => handleRotate()} style={styles.rotateIcon}>
                  <RotateRightIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        )}
        {activeRatePlan && activeRatePlan.rate_plan_fine_types.length ? (
          // the active rate-plan, in this case, is the rate plan of the fleet where the ride ends
          // see useEffect above
          <PermissionedDisplay permission={[Permissions.issueFine]}>
            <Grid item xs={12}>
              <Button
                component={RouterLink}
                style={styles.kibanaButton}
                data-testid={testIds.issueFineButton}
                to={{
                  pathname: `/riders/${riderId}/reservations/${reservationId}/issue-fine`,
                  state: { background: location },
                }}
              >
                {t("Riders.FineModal.IssueFine")}
              </Button>
            </Grid>
          </PermissionedDisplay>
        ) : null}
      </Grid>
      <Grid item container xs={12} md={4} style={{ paddingLeft: 10, paddingRight: 40 }}>
        <Receipt />
      </Grid>
    </SectionGridContainer>
  );
};

export default ReservationDetail;
